import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'
import { registerSW } from 'virtual:pwa-register'

// Register service worker with auto-update.
// When a new version is available, it will be activated on the next page load.
registerSW({ immediate: true })

const RiffDraftPreset = definePreset(Aura, {
    semantic: {
        colorScheme: {
            light: {
                success: {
                    color: '{teal.500}',
                    inverseColor: '#ffffff',
                    hoverColor: '{teal.600}',
                    activeColor: '{teal.700}'
                }
            }
        }
    }
})
import Tooltip from 'primevue/tooltip'
import 'primeicons/primeicons.css'
import './style.css'
import App from './App.vue'
import { useUserStore } from './stores/userStore'

// Wrap PrimeVue Tooltip to inject a 500ms showDelay on every tooltip.
// PrimeVue stores the delay as el.$_ptooltipShowDelay on the target element.
// We patch this property after each original hook runs.
const TOOLTIP_DELAY = 500
const patchDelay = (el: HTMLElement) => {
  const target = el as any
  // PrimeVue may resolve target to a child input for p-inputwrapper elements
  // but for most cases it's the element itself
  if (target.$_ptooltipShowDelay !== undefined) {
    target.$_ptooltipShowDelay = TOOLTIP_DELAY
  }
  // Also check first child input (for InputText wrappers)
  const input = el.querySelector?.('input') as any
  if (input?.$_ptooltipShowDelay !== undefined) {
    input.$_ptooltipShowDelay = TOOLTIP_DELAY
  }
}

const DelayedTooltip: Record<string, any> = {}
for (const hook of Object.keys(Tooltip)) {
  const original = (Tooltip as any)[hook]
  if (typeof original === 'function') {
    DelayedTooltip[hook] = function (this: any, el: HTMLElement, binding: any, vnode: any, prevVnode: any) {
      const result = original.call(this, el, binding, vnode, prevVnode)
      if (hook === 'beforeMount' || hook === 'mounted' || hook === 'updated') {
        patchDelay(el)
      }
      return result
    }
  }
}

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.directive('tooltip', DelayedTooltip)

const userStore = useUserStore()
userStore.init()

// Initialize notification store (detects browser support, reads current permission state).
// Must be after Pinia is created but doesn't require a user — safe to run early.
import { useNotificationStore } from './stores/notificationStore'
useNotificationStore().init()

app.use(PrimeVue, {
    theme: {
        preset: RiffDraftPreset,
        options: {
            darkModeSelector: '.dark-mode'
        }
    }
})

app.mount('#app')
